import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import useGame from '../stores/useGame.jsx'

/**
 * FloatIntensityController
 * Manages smooth transitions of floatIntensityMultiplier:
 * - Starts at 1.0 on app load
 * - Lerps to 0.0 when any SEC is selected (during camera transition)
 * - After 3 seconds in default view, lerps back to 1.0 over 2 seconds
 */
export function FloatIntensityController() {
  const viewState = useGame((state) => state.viewState)
  const setFloatIntensityMultiplier = useGame((state) => state.setFloatIntensityMultiplier)
  
  const currentValueRef = useRef(1.0)
  const targetValueRef = useRef(1.0)
  const defaultViewTimeRef = useRef(0)
  const lerpSpeedRef = useRef(2.0) // Speed for lerping (adjust for faster/slower transitions)
  
  // Initialize multiplier to 1.0 on mount
  useEffect(() => {
    setFloatIntensityMultiplier(1.0)
    currentValueRef.current = 1.0
    targetValueRef.current = 1.0
  }, [setFloatIntensityMultiplier])
  
  // Handle viewState changes
  useEffect(() => {
    if (viewState === 'sec_zoomed') {
      targetValueRef.current = 0.0
      lerpSpeedRef.current = 1.0
      defaultViewTimeRef.current = 0
    } else if (viewState === 'default_state') {
      // Returned to default: start 3-second timer before lerping back up
      defaultViewTimeRef.current = 0
      targetValueRef.current = 0.0 // Keep at 0 until timer elapses
    }
  }, [viewState])
  
  // Frame-by-frame lerping
  useFrame((state, delta) => {
    // If in default view, accumulate time
    if (viewState === 'default_state') {
      defaultViewTimeRef.current += delta
      
      // After 3 seconds in default view, start lerping back to 1.0
      if (defaultViewTimeRef.current >= 3.0 && targetValueRef.current !== 1.0) {
        targetValueRef.current = 1.0
        lerpSpeedRef.current = 0.5
      }
    }
    
    // Perform lerp if target differs from current
    if (Math.abs(currentValueRef.current - targetValueRef.current) > 0.001) {
      const lerpFactor = Math.min(delta * lerpSpeedRef.current, 1.0)
      currentValueRef.current += (targetValueRef.current - currentValueRef.current) * lerpFactor
      
      // Clamp to target when very close
      if (Math.abs(currentValueRef.current - targetValueRef.current) < 0.001) {
        currentValueRef.current = targetValueRef.current
      }
      
      // Update store
      setFloatIntensityMultiplier(currentValueRef.current)
    }
  })
  
  return null
}
